import React, { useState, useEffect } from 'react';
import {
  Calendar, Save, User, ExternalLink
} from 'lucide-react';
import { candidatesApi } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { useSofteamboardStore } from '../../store/softeamboardStore';
import { normalizeStep as storeNormalizeStep } from '../../store/softeamboardStore';
import {
  validateStepProgression,
  validateNoDuplicateSteps,
  validateStepDates,
  getNextAllowedSteps,
  StepValidationError
} from '../../utils/stepValidation';
import { Modal, ModalActions, Alert } from '../shared';
import StepDetailForm from './StepDetailForm';

const AddStepModal = ({ needId, candidateId, open, onClose, onStepCreated }) => {
  const { user } = useAuth();
  const { addStepToCache } = useSofteamboardStore(); // ✅ NEW: Get store function
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [existingSteps, setExistingSteps] = useState([]);
  const [allowedSteps, setAllowedSteps] = useState([]);
  
  const [formData, setFormData] = useState({
    type: 'CANDIDATE_IDENTIFIED',
    date: new Date().toISOString().slice(0, 16),
    comment: '',
    propositionUrl: '',
    
    // CANDIDATE_SENT details
    candidateSent: {
      clientName: '',
      clientEmail: '',
      mailSentDate: new Date().toISOString().slice(0, 16),
      followUpDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().slice(0, 16)
    },
    
    // CANDIDATE_INTERVIEW details
    candidateInterview: {
      invitationDate: new Date().toISOString().slice(0, 16),
      interviewDate: new Date(Date.now() + 3 * 24 * 60 * 60 * 1000).toISOString().slice(0, 16),
      interviewLocation: ''
    },
    
    // GO_NOGO details
    goNoGo: {
      decision: 'GO',
      projectedStartDate: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString().slice(0, 16),
      tjm: '',
      commercialConcession: '',
      endDate: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().slice(0, 16)
    },
    
    // SALE details
    sale: {
      tjm: '',
      commercialConcession: '',
      contractType: 'CDI',
      startDate: new Date().toISOString().slice(0, 16),
      endDate: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().slice(0, 16)
    }
  });

  const stepTypes = [
    { value: 'CANDIDATE_IDENTIFIED', label: 'Candidat identifié', hasDetails: false },
    { value: 'CANDIDATE_SENT', label: 'Candidat envoyé au client', hasDetails: true },
    { value: 'CANDIDATE_INTERVIEW', label: 'Entretien planifié', hasDetails: true },
    { value: 'GO_NOGO', label: 'Décision GO / NO-GO', hasDetails: true },
    { value: 'SALE', label: 'Vente finalisée', hasDetails: true }
    // Removed: FROZEN and CANCELED - these are terminal states that shouldn't be manually created
  ];

  useEffect(() => {
    if (open && needId && candidateId) {
      loadExistingSteps();
    }
  }, [open, needId, candidateId]);

  const loadExistingSteps = async () => {
    try {
      const response = await candidatesApi.getStepsForProcess(needId, candidateId);
      
      // Fix: Extract the steps array from the response object
      const steps = response.steps || [];
      setExistingSteps(steps);
      
      const allowed = getNextAllowedSteps(steps, user?.role || 'Recruiter')
        .filter(type => stepTypes.some(step => step.value === type));
      setAllowedSteps(allowed);
      
      if (allowed.length > 0) {
        setFormData(prev => ({ ...prev, type: allowed[0] }));
      }
    } catch (error) {
      console.error('Error loading existing steps:', error);
      setAllowedSteps(['CANDIDATE_IDENTIFIED']);
      setFormData(prev => ({ ...prev, type: 'CANDIDATE_IDENTIFIED' }));
    }
  };

  const normalizeCreatedStep = (step, fallbackDetails = {}) => {
    const normalized = storeNormalizeStep(step);
    return {
      ...normalized,
      detailCandidateSent: normalized.detailCandidateSent || fallbackDetails.detailCandidateSent || null,
      detailCandidateInterview: normalized.detailCandidateInterview || fallbackDetails.detailCandidateInterview || null,
      detailGoNoGo: normalized.detailGoNoGo || fallbackDetails.detailGoNoGo || null,
      detailSale: normalized.detailSale || fallbackDetails.detailSale || null
    };
  };

  const handleInputChange = (field, value, section = null) => {
    if (section) {
      setFormData(prev => ({
        ...prev,
        [section]: {
          ...prev[section],
          [field]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  const getSelectedStepType = () => {
    return stepTypes.find(type => type.value === formData.type);
  };

  const validateForm = () => {
    try {
      if (!formData.type) {
        setError('Step type is required');
        return false;
      }

      if (!formData.date) {
        setError('Date is required');
        return false;
      }

      validateStepProgression(existingSteps, formData.type);
      validateNoDuplicateSteps(existingSteps, formData.type);

      const selectedType = getSelectedStepType();
      
      if (selectedType?.hasDetails) {
        switch (formData.type) {
          case 'CANDIDATE_SENT':
            validateStepDates(formData.type, formData.candidateSent);
            break;

          case 'CANDIDATE_INTERVIEW':
            validateStepDates(formData.type, formData.candidateInterview);
            break;

          case 'GO_NOGO':
            if (!formData.goNoGo.decision) {
              setError('Decision is required for Go/No-Go step');
              return false;
            }
            validateStepDates(formData.type, formData.goNoGo);
            break;

          case 'SALE':
            if (!formData.sale.tjm || Number.isNaN(Number.parseFloat(formData.sale.tjm))) {
              setError('Valid TJM is required for sale step');
              return false;
            }
            if (!formData.sale.contractType) {
              setError('Contract type is required for sale step');
              return false;
            }
            validateStepDates(formData.type, formData.sale);
            break;
        }
      }

      return true;
    } catch (error) {
      if (error instanceof StepValidationError) {
        setError(error.message);
      } else {
        setError('Validation error: ' + error.message);
      }
      return false;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    if (!validateForm()) {
      setLoading(false);
      return;
    }

    try {
      if (!allowedSteps.includes(formData.type)) {
        throw new Error(`Step type ${formData.type} is not allowed at this stage`);
      }

      const selectedType = getSelectedStepType();
      
      const stepData = {
        type: formData.type,
        date: new Date(formData.date).toISOString(),
        comment: formData.comment.trim() || null,
        propositionUrl: formData.propositionUrl.trim() || null
      };

      if (selectedType?.hasDetails) {
        switch (formData.type) {
          case 'CANDIDATE_SENT':
            stepData.detailCandidateSent = {
              clientName: formData.candidateSent.clientName.trim() || null,
              clientEmail: formData.candidateSent.clientEmail.trim() || null,
              mailSentDate: new Date(formData.candidateSent.mailSentDate).toISOString(),
              followUpDate: new Date(formData.candidateSent.followUpDate).toISOString()
            };
            break;

          case 'CANDIDATE_INTERVIEW':
            stepData.detailCandidateInterview = {
              invitationDate: new Date(formData.candidateInterview.invitationDate).toISOString(),
              interviewDate: new Date(formData.candidateInterview.interviewDate).toISOString(),
              interviewLocation: formData.candidateInterview.interviewLocation
            };
            break;

          case 'GO_NOGO':
            stepData.detailGoNoGo = {
              decision: formData.goNoGo.decision,
              projectedStartDate: formData.goNoGo.projectedStartDate ? new Date(formData.goNoGo.projectedStartDate).toISOString() : null,
              tjm: formData.goNoGo.tjm ? Number.parseFloat(formData.goNoGo.tjm) : null,
              commercialConcession: formData.goNoGo.commercialConcession.trim() || null,
              endDate: formData.goNoGo.endDate ? new Date(formData.goNoGo.endDate).toISOString() : null
            };
            break;

          case 'SALE':
            stepData.detailSale = {
              tjm: Number.parseFloat(formData.sale.tjm),
              commercialConcession: formData.sale.commercialConcession.trim() || '',
              contractType: formData.sale.contractType,
              startDate: new Date(formData.sale.startDate).toISOString(),
              endDate: new Date(formData.sale.endDate).toISOString()
            };
            break;
        }
      }

      const createdStep = await candidatesApi.createStep(needId, candidateId, stepData);
      const normalizedStep = normalizeCreatedStep(createdStep, {
        detailCandidateSent: stepData.detailCandidateSent,
        detailCandidateInterview: stepData.detailCandidateInterview,
        detailGoNoGo: stepData.detailGoNoGo,
        detailSale: stepData.detailSale
      });
      
      // ✅ NEW: Update store cache immediately
      addStepToCache(needId, candidateId, normalizedStep);
      
      // ✅ KEEP: Call the original callback for backward compatibility
      onStepCreated?.(normalizedStep);
      onClose();
    } catch (err) {
      console.error('Error creating step:', err);
      if (err instanceof StepValidationError) {
        setError(err.message);
      } else {
        setError(err.message || 'Failed to create step');
      }
    } finally {
      setLoading(false);
    }
  };

  const availableStepTypes = stepTypes.filter(step => 
    allowedSteps.includes(step.value)
  );

  const renderStepDetails = () => {
    const selectedType = getSelectedStepType();

    if (!selectedType?.hasDetails) {
      return (
        <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
          <div className="flex items-center space-x-2 text-blue-800">
            <User size={16} />
            <span className="font-medium">{selectedType?.label}</span>
          </div>
          <p className="text-sm text-blue-600 mt-2">{selectedType?.label}</p>
          <p className="text-xs text-blue-500 mt-1">
            Cette étape met à jour l'avancement sans information complémentaire.
          </p>
        </div>
      );
    }

    return <StepDetailForm stepType={formData.type} formData={formData} onInputChange={handleInputChange} />;
  };

  const selectedType = getSelectedStepType();

  const subtitle = existingSteps.length > 0
    ? 'Choisissez uniquement parmi les prochaines étapes autorisées.'
    : 'Créez la première étape du parcours de recrutement.';

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Ajouter une étape"
      subtitle={subtitle}
      icon={Calendar}
    >
      <form onSubmit={handleSubmit} className="p-6 space-y-6">
            <div className="grid gap-3 rounded-lg border border-blue-200 bg-blue-50 p-4 text-sm sm:grid-cols-[1fr_auto_1fr] sm:items-center">
              <div>
                <span className="block text-xs font-medium uppercase tracking-wide text-blue-600">Étape actuelle</span>
                <strong className="mt-1 block text-blue-900">{existingSteps[0]?.type || 'Aucune étape'}</strong>
              </div>
              <span className="hidden text-blue-400 sm:block">→</span>
              <div>
                <span className="block text-xs font-medium uppercase tracking-wide text-blue-600">Étapes autorisées</span>
                <strong className="mt-1 block text-blue-900">{availableStepTypes.map(step => step.label).join(', ') || 'Aucune'}</strong>
              </div>
            </div>

            {error && (
              <Alert variant="error">{error}</Alert>
            )}

            {availableStepTypes.length === 0 && (
              <Alert variant="warning">
                  Aucune étape disponible. Votre rôle ({user?.role}) ne permet peut-être pas de faire avancer ce profil.
              </Alert>
            )}

            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-900">Informations de l'étape</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="add-step-type" className="block text-sm font-medium text-gray-700 mb-1">
                    Type d'étape *
                  </label>
                  <select id="add-step-type"
                    value={formData.type}
                    onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  >
                    <option value="">Sélectionner une étape...</option>
                    {availableStepTypes.map(step => (
                      <option key={step.value} value={step.value}>
                        {step.label}
                      </option>
                    ))}
                  </select>
                  {selectedType && (
                    <p className="text-xs text-gray-500 mt-1">{selectedType.label}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="add-step-date" className="block text-sm font-medium text-gray-700 mb-1">
                    Date et heure *
                  </label>
                  <input id="add-step-date"
                    type="datetime-local"
                    value={formData.date}
                    onChange={(e) => handleInputChange('date', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  />
                </div>
              </div>

              <div>
                <label htmlFor="add-step-comment" className="block text-sm font-medium text-gray-700 mb-1">
                  Commentaire
                </label>
                <textarea id="add-step-comment"
                  value={formData.comment}
                  onChange={(e) => handleInputChange('comment', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  rows={3}
                  placeholder="Ajouter une note utile au suivi..."
                />
              </div>

            </div>

            {selectedType && renderStepDetails()}

            <details className="rounded-lg border border-gray-200 bg-white">
              <summary className="min-h-[44px] cursor-pointer px-4 py-3 font-medium text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-600">
                Intégration Salesforce (optionnel)
              </summary>
              <div className="border-t border-gray-200 p-4">
                <label htmlFor="add-step-proposition-url" className="mb-1 block text-sm font-medium text-gray-700">URL de la proposition Salesforce</label>
                <div className="flex">
                  <input id="add-step-proposition-url" type="url" value={formData.propositionUrl} onChange={(e) => handleInputChange('propositionUrl', e.target.value)} className="flex-1 rounded-l-lg border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-blue-500" placeholder="https://domain.lightning.force.com/..." />
                  {formData.propositionUrl && <button type="button" onClick={() => window.open(formData.propositionUrl, '_blank')} className="min-h-[44px] min-w-[44px] rounded-r-lg bg-blue-600 px-3 text-white hover:bg-blue-700" title="Ouvrir la proposition dans Salesforce"><ExternalLink className="h-4 w-4" /></button>}
                </div>
              </div>
            </details>

            <ModalActions
              onCancel={onClose}
              loading={loading}
              submitLabel="Créer l'étape"
              loadingLabel="Création..."
              submitIcon={Save}
              disabled={availableStepTypes.length === 0}
            />
          </form>
    </Modal>
  );
};

export default AddStepModal;
